import React, { useState } from "react";
import debug from "debug";
import { ClickAwayListener, Input, InputAdornment, Popper } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { arrayToTree, filterTreeDataByString, TreeItem } from "@bonliva-crm/common";
import { hasClassInParent } from "@bonliva-crm/ui";
import { MenuRendererProps, Option } from "./utils";

const log = debug("app:Select:NestedGroupedMenuRenderer");

type Props = MenuRendererProps & {
  expanded?: boolean;
  isOpen: boolean;
  onClose: () => void;
  showSearchInput?: boolean;
  disablePortal: boolean;
};

export function NestedGroupedMenuRenderer({
  disablePortal = true,
  showSearchInput = true,
  ...props
}: Props) {
  const [searchValue, setSearchValue] = useState("");
  const [groupOpenItems, setGroupOpenItems] = useState<
    Array<{ id: string; isGroupOpen: boolean }>
  >([
    ...props.options.map((x) => ({
      id: x.value,
      isGroupOpen: x.isGroupOpen ?? !!props.expanded
    }))
  ]);

  const anchor = document.getElementById(`${props.instancePrefix}-value`).parentElement
    .parentElement;

  const anchorWidth = anchor.getBoundingClientRect().width;

  const OptionCmp = props.optionComponent;

  const tree = arrayToTree(
    props.options.map((x) => ({ ...x, id: x.value, parentId: x.parent })),
    {
      id: (x) => x.id,
      parentId: (x) => x.parentId
    }
  );

  function renderNode(node: TreeItem<Option>, index, filter, level = 0) {
    const groupOpen = groupOpenItems.find((x) => x.id === node.id);
    const isGroup = node.children.length > 0;
    const isParentSelected = (item) => {
      return node.value.parent?.includes(item.value?.replace("||||||", "|||"));
    };

    const isSelected = !!props.valueArray.find((x) => {
      return x.value === node.value.value || isParentSelected(x);
    });

    const disabled = !!props.valueArray.find(isParentSelected);
    return (
      <React.Fragment key={`${index}-${level}`}>
        <OptionCmp
          key={`option-${index}-${node.value}`}
          focusOption={props.focusOption}
          inputValue={props.inputValue || searchValue}
          instancePrefix={props.instancePrefix}
          isDisabled={node.value.disabled || disabled}
          isFocused={
            props.focusedOption && node.value.value === props.focusedOption.value
          }
          isSelected={isSelected}
          onFocus={props.onFocus}
          onSelect={props.onSelect}
          parentLevel={level}
          option={node.value}
          isGroup={isGroup}
          handleDropdownItemClick={(e) => {
            e.stopPropagation();
            setGroupOpenItems([
              ...groupOpenItems.map((x) => {
                if (x.id === node.id) {
                  return { ...x, isGroupOpen: !x.isGroupOpen };
                } else {
                  return x;
                }
              })
            ]);
          }}
          isGroupOpen={groupOpen?.isGroupOpen}
          removeValue={props.removeValue}
          selectValue={props.selectValue}
        >
          {node.value.label}
        </OptionCmp>

        {groupOpen?.isGroupOpen &&
          (node.children || []).map((x, i) => renderNode(x, i, filter, level + 1))}
      </React.Fragment>
    );
  }

  const filteredTree = filterTreeDataByString<Option>(
    tree,
    searchValue,
    (x) => x.label as string
  );
  const items = filteredTree.map((option, i) => renderNode(option, i, searchValue, 0));

  return (
    <Popper
      open
      id={props.instancePrefix}
      anchorEl={anchor}
      style={{
        zIndex: 9999
      }}
      disablePortal={disablePortal}
      placement="bottom-start"
    >
      <ClickAwayListener
        mouseEvent="onMouseDown"
        onClickAway={(e) => {
          log("onClickAway", e);

          if (hasClassInParent(e.target, "Select-control")) {
            return;
          }

          props.onClose();
        }}
      >
        <div className="Select-menu-dropdown" style={{ minWidth: anchorWidth }}>
          {showSearchInput && (
            <div style={{ padding: "16px 24px" }}>
              <Input
                autoFocus
                fullWidth
                startAdornment={
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                }
                inputProps={{
                  onFocus: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                value={searchValue}
                onChange={({ target: { value } }) => {
                  log("search item filter");
                  const parents = props.options
                    .filter((x) => x.value.includes(value))
                    .map((x) => x.parent);
                  setGroupOpenItems(
                    groupOpenItems.map((x) => {
                      if (parents.any((y) => y === x.id)) {
                        return { ...x, isGroupOpen: true };
                      } else {
                        return x;
                      }
                    })
                  );
                  setSearchValue(value);
                }}
              />
            </div>
          )}
          {items}
        </div>
      </ClickAwayListener>
    </Popper>
  );
}
